/**
 * The brain autopilot's own status, polled only while a batch is running.
 *
 * Its own hook rather than a few lines inside the list page, because the page
 * and the engine fail separately and must keep failing separately: a card whose
 * endpoint is down may not blank the list of companies next to it, and a list
 * that failed to load must still let an operator switch the engine off. That is
 * also why the loader swallows its error instead of surfacing one.
 *
 * WHILE A BATCH RUNS, THE LIST IS PULLED IN WITH IT. A brain batch takes minutes
 * and its whole visible effect is new rows appearing below, so refreshing the
 * engine's numbers without refreshing the list would show a counter climbing
 * over a list that never changes. The polling stops the moment the batch does:
 * a page nobody is watching should not be asking for updates.
 */
import { useCallback, useEffect, useState } from "react";
import { getBrainAutopilot, type BrainAutopilotState } from "../../../lib/siteBrain";

/** How often to re-read the engine while it is working. */
const BUSY_POLL_MS = 5000;

export function useBrainAutopilot(onBatchTick: () => void) {
  const [state, setState] = useState<BrainAutopilotState | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await getBrainAutopilot());
    } catch {
      // Informational. The list of companies stands on its own and must not go
      // blank because this one endpoint failed.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!state?.busy) return;
    const timer = setInterval(() => {
      void load();
      onBatchTick();
    }, BUSY_POLL_MS);
    return () => clearInterval(timer);
  }, [state?.busy, load, onBatchTick]);

  return { state, refresh: load };
}
